
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { HeaderComponent } from "@components/header/header.component";
import _ from "lodash";
import moment from "moment";
import { Subject, takeUntil } from "rxjs";
import { map } from "rxjs/operators";
import { SkogsvardMattillfallen } from "~app/common/models/constants";
import { TradslagDTO } from "~app/common/models/tradslagsdata/TradslagDTO";
import { DateUtils } from "~app/common/utils/date-utils";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { UpdateSkogsvardTradskiktCommand } from "~app/features/virkeskop/commands/skogsvard-tradskikt/UpdateSkogsvardTradskiktCommand";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { VKAtgardRojningTradskikt, VKAtgardRojningTradslag } from "~app/features/virkeskop/models/skogsvard/skogsvard-models";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { KontraktCommandService } from "~app/features/virkeskop/services/kontrakt-command.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { SkogsvardService } from "~app/features/virkeskop/services/skogsvard.service";
import { canEdit } from "~app/role-permissions";
import { SkogsvardTradskiktTradslagComponent } from "./skogsvard-tradskikt-tradslag/skogsvard-tradskikt-tradslag.component";
@Component({
    selector: "rask-skogsvard-tradskikt",
    templateUrl: "./skogsvard-tradskikt.component.html",
    styleUrl: "./skogsvard-tradskikt.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        DatepickerComponent,
        FormsModule,
        SkogsvardTradskiktTradslagComponent
    ]
})
export class SkogsvardTradskiktComponent implements OnDestroy, OnInit {
    protected skogsvardsAtgard: Atgard;
    protected allaTradslag: TradslagDTO[];
    protected kontraktReadonly = false;
    protected foreRojning: VKAtgardRojningTradskikt;
    protected efterRojning: VKAtgardRojningTradskikt;
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly skogsvardService = inject(SkogsvardService);
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly destroy$ = new Subject<void>();

    private readonly canEdit = canEdit();

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.atgardMediatorService.atgard$
            .pipe(
                takeUntil(this.destroy$)
            ).subscribe(atgard => this.skogsvardsAtgard = atgard);

        this.kontraktMediatorService.isReadonly$
            .pipe(
                map(readonly => readonly || !this.canEdit()),
                takeUntil(this.destroy$))
            .subscribe(readonly => this.kontraktReadonly = readonly);

        if (!this.skogsvardsAtgard.id) {
            this.mapTradskikt([]);
        } else {
            this.skogsvardService.getRojningTradskikt(this.skogsvardsAtgard.id)
                .pipe(
                    takeUntil(this.destroy$)
                ).subscribe(tradskiktDtos => {
                    const tradskikt = tradskiktDtos.map(dto => new VKAtgardRojningTradskikt(dto));
                    this.mapTradskikt(tradskikt);
                });
        }

        this.skogsvardService.getTradslag().pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (tradslag) => this.allaTradslag = _.orderBy(tradslag, t => t.sekvensnummer),
            error: () => this.allaTradslag = []
        });
    }

    protected matdatumChanged(datum: string, rojningTradskikt: VKAtgardRojningTradskikt): void {
        if (rojningTradskikt?.matdatum === null && _.isNil(datum)) {
            return;
        }

        rojningTradskikt.matdatum = _.isNil(datum) ? null : moment(datum).format(DateUtils.dateformat);
        this.addCommand();
    }

    protected handleUpdates(rojningTradslag: VKAtgardRojningTradskikt): void {
        if (rojningTradslag.mattillfalle === SkogsvardMattillfallen.foreAtgard) {
            this.foreRojning = rojningTradslag;
        } else {
            this.efterRojning = rojningTradslag;
        }
        this.addCommand();
    }

    protected getMatDatum(rojningTradskikt: VKAtgardRojningTradskikt): string {
        if (!_.isNil(rojningTradskikt?.matdatum)) {
            return moment(rojningTradskikt.matdatum).format(DateUtils.dateformat);
        }

        return null;
    }

    private mapTradskikt(tradskikt: VKAtgardRojningTradskikt[]): void {
        this.foreRojning = tradskikt.find(ts => ts.mattillfalle === SkogsvardMattillfallen.foreAtgard);
        this.efterRojning = tradskikt.find(ts => ts.mattillfalle === SkogsvardMattillfallen.beraknatKvar);

        if (!this.foreRojning) {
            this.foreRojning = new VKAtgardRojningTradskikt(null);
            this.foreRojning.guid = GuidUtils.newGuid();
            this.foreRojning.mattillfalle = SkogsvardMattillfallen.foreAtgard;
            this.foreRojning.tradslag = [];
        }

        if (!this.efterRojning) {
            this.efterRojning = new VKAtgardRojningTradskikt(null);
            this.efterRojning.guid = GuidUtils.newGuid();
            this.efterRojning.mattillfalle = SkogsvardMattillfallen.beraknatKvar;
            this.efterRojning.tradslag = [];
        }

        this.foreRojning.tradslag = this.orderBy([...this.foreRojning.tradslag]);
        this.efterRojning.tradslag = this.orderBy([...this.efterRojning.tradslag]);
    }

    private addCommand(): void {
        const tradskiktList = [
            this.foreRojning,
            this.efterRojning
        ];
        const cmd = new UpdateSkogsvardTradskiktCommand(this.skogsvardsAtgard.guid, tradskiktList);
        this.kontraktCommandService.update(cmd);
    }

    private orderBy(tradslag: VKAtgardRojningTradslag[]): VKAtgardRojningTradslag[] {
        return _.orderBy(tradslag, ["sekvensnummer"], ["asc"]);
    }
}
